import { FormApi } from "final-form";
import _ from "lodash";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import { TextareaField } from "src/ui/shared/form/TextareaField";
import { Modal } from "src/ui/shared/Modal/Modal";
import { showSuccessToast } from "src/ui/shared/toasts/Toasts";

import {
    useFetchProductChangeLogsQuery,
    useUpdateBuildMetadataMutation,
    useUpdateBuildRolloutMutation,
    useUpdateProductChangeLogMutation,
} from "@dashboard/products/api";
import { Build } from "@dashboard/products/types";
import { TagManagementSection } from "@dashboard/shared/components/TagManagementSection";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { Box, DialogProps, Tab, Typography, useTheme } from "@mui/material";
import CompareService from "@services/compareService/compareService";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";

import { buildVariantOptions } from "../../ProductBuilder/components/Recipes";
import { RolloutField, validateRollout } from "../../shared/buildConfigFields";
import { BuildVariantLabel } from "../shared";

import { OtaFilesList } from "./OtaTab/OtaFilesList";
import { useStyles as useOtaModalStyles } from "./OtaTab/OtaModal.style";
import { useStyles } from "./BuildSettingsDialog.style";

const snapshotLabel = "configSnapshot ";
const overrideLabel = "configOverride ";

type FormValues = {
    tagSection: API.Tag[];
    changeLogSection: string | null;
    rolloutSection: number;
    buildVariant: string;
};

const validate = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};

    const rolloutError = validateRollout(values.rolloutSection);
    if (rolloutError) {
        errors.rolloutSection = rolloutError;
    }
    return errors;
};
const getFormDiff = (form: FormApi<FormValues>) => {
    const rolloutChanged = form.getFieldState("rolloutSection")?.dirty;
    const changeLogChanged = form.getFieldState("changeLogSection")?.dirty;
    const tagsChanged = new CompareService(
        _.keyBy(form.getState().initialValues?.tagSection, "key"),
        _.keyBy(form.getState().values?.tagSection, "key"),
    ).hasDifferences;
    const pristine = !changeLogChanged && !tagsChanged && !rolloutChanged;
    return { changeLogChanged, tagsChanged, rolloutChanged, pristine };
};
const getBuildVariant = (build: Build): string => {
    if (build.configOverride && "buildVariant" in build.configOverride) {
        return build.configOverride.buildVariant as string;
    }

    if (build.configSnapshot && build.configSnapshot?.buildVariant) {
        return build.configSnapshot.buildVariant;
    }

    return "-";
};

const BuildSettingsForm = ({ values, form }: { values: FormValues | undefined; form: FormApi<FormValues> }) => {
    const { classes } = useStyles();
    const theme = useTheme();
    const { t } = useTranslation(["modals"]);

    return (
        <>
            <Box className={classes.sectionSpacing}>
                <TagManagementSection tags={values?.tagSection || []} onChange={(data) => form.change("tagSection", data)} />
            </Box>
            <Box className={classes.sectionSpacing}>
                <TextareaField
                    name="changeLogSection"
                    label={t("modals:buildConfig.input.releaseNotes.captionChanges")}
                    minRows={4}
                    placeholder={t("modals:buildConfig.input.releaseNotes.placeholder")}
                    fullWidth
                />
            </Box>
            <Box className={classes.sectionSpacing}>
                <RolloutField name="rolloutSection" />
            </Box>
            <Box className={`${classes.variantBox} ${classes.sectionSpacing}`}>
                <Box className={classes.variantSelectBox}>
                    <ControlledSelectField
                        name="buildVariant"
                        label={t("modals:buildConfig.input.buildVariant.caption")}
                        disabled
                        options={buildVariantOptions}
                        style={{ marginRight: theme.spacing(1) }}
                        fullWidth
                    />
                    <BuildVariantLabel />
                </Box>
                <Typography className={classes.hintText}>{t("modals:buildSettings.buildVariantCannotBeChanged")}</Typography>
            </Box>
        </>
    );
};

type Props = Omit<DialogProps, "onClose"> & {
    build: Build;
    onClose: (event?: object) => void;
};

type TabValue = "settings" | "config" | "ota";

export const BuildSettingsDialog = ({ build, ...props }: Props) => {
    const [tabValue, setTabValue] = React.useState<TabValue>("settings");

    const { classes } = useStyles();
    const { classes: otaClasses } = useOtaModalStyles();
    const { t } = useTranslation(["modals", "common"]);

    const { data: changeLogs } = useFetchProductChangeLogsQuery(build.productId);
    const [updateProductChangeLog] = useUpdateProductChangeLogMutation();
    const [updateBuildMetadata] = useUpdateBuildMetadataMutation();
    const [updateBuildRollout] = useUpdateBuildRolloutMutation();

    const initialValues: FormValues = {
        changeLogSection: changeLogs?.[build.version]?.join("\n") || "",
        tagSection: build.metadata?.user?.tags || [],
        rolloutSection: build.rolloutPercentage,
        buildVariant: getBuildVariant(build),
    };

    const otaFiles = build.artifacts?.filter((artifact) => artifact.type === "ota") || [];
    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>, form: FormApi<FormValues>) => {
        const { submitting, active } = form.getState();
        const { pristine } = getFormDiff(form);

        if (e.key === "Enter" && !pristine && !submitting && !active) {
            e.preventDefault();
            await form.submit();
        }

        if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !pristine && !submitting && active === "changeLogSection") {
            e.preventDefault();
            await form.submit();
        }
    };

    const onSubmit = async (values: FormValues, form: FormApi<FormValues>) => {
        const { changeLogChanged, tagsChanged, rolloutChanged } = getFormDiff(form);

        if (changeLogChanged) {
            const result = await updateProductChangeLog({
                productId: build.productId,
                version: build.version,
                changes: values.changeLogSection?.split("\n").filter((item) => item.trim() !== "") || [],
            });
            if ("error" in result) {
                return;
            }
        }
        if (tagsChanged) {
            const result = await updateBuildMetadata({
                productId: build.productId,
                buildId: build.buildId,
                metadata: { ...build.metadata?.user, tags: values.tagSection },
            });
            if ("error" in result) {
                return;
            }
        }
        if (rolloutChanged) {
            const result = await updateBuildRollout({
                productId: build.productId,
                buildId: build.buildId,
                rolloutPercentage: +values.rolloutSection,
            });
            if ("error" in result) {
                return;
            }
        }

        showSuccessToast(t("modals:buildSettings.toast.buildSettingsUpdatedSuccessfully"));
        props.onClose();
    };

    const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
        setTabValue(value as TabValue);
    };

    return (
        <Form<FormValues>
            {...{ initialValues, validate, onSubmit }}
            render={({ submitting, valid, form, values }) => (
                <Modal
                    {...props}
                    title={t("modals:buildSettings.buildSettings")}
                    onKeyDown={tabValue === "settings" ? (e) => onKeyDown(e, form) : undefined}
                    endButton={
                        tabValue === "settings" ? (
                            <PrimaryButton
                                onClick={form.submit}
                                disabled={submitting || !valid || getFormDiff(form).pristine}
                                loading={submitting}
                                tooltipProps={{ title: !valid ? "You have validation issues" : "" }}
                            >
                                {t("common:button.save")}
                            </PrimaryButton>
                        ) : null
                    }
                >
                    <TabContext value={tabValue}>
                        <TabList onChange={handleTabChange} aria-label="build settings tabs">
                            <Tab label={t("modals:buildSettings.generalTab")} value="settings" />
                            <Tab label={t("modals:buildSettings.otaFiles")} value="ota" />
                            <Tab label={t("modals:buildSettings.configTab")} value="config" />
                        </TabList>
                        <Box className={classes.tabPanels}>
                            <TabPanel value="settings" className={classes.tabPanel}>
                                <BuildSettingsForm values={values} form={form} />
                            </TabPanel>
                            <TabPanel value="ota" className={classes.tabPanel}>
                                <Box className={otaClasses.modalContent}>
                                    {otaFiles.length > 0 ? (
                                        <OtaFilesList otaFiles={otaFiles} />
                                    ) : (
                                        <Box className={otaClasses.emptyState}>
                                            <Typography variant="body2" textAlign="center">
                                                {t("modals:otaFiles.noFilesDescription")}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </TabPanel>
                            <TabPanel value="config" className={classes.tabPanel}>
                                {!build.configSnapshot && !build.configOverride ? (
                                    <Typography variant="body1">{t("modals:buildSettings.noCustomBuildConfig")}</Typography>
                                ) : (
                                    <Box>
                                        {build.configSnapshot && (
                                            <Box component="pre" className={classes.container}>
                                                {snapshotLabel} {JSON.stringify(build.configSnapshot, null, 2)}
                                            </Box>
                                        )}
                                        {build.configOverride && (
                                            <Box component="pre" className={classes.container}>
                                                {overrideLabel} {JSON.stringify(build.configOverride, null, 2)}
                                            </Box>
                                        )}
                                    </Box>
                                )}
                            </TabPanel>
                        </Box>
                    </TabContext>
                </Modal>
            )}
        />
    );
};
